import { memo, useCallback, useEffect, useState } from 'react';
import cn from 'classnames';

import { Modal, RadioButton, SpinnerOverlay } from '@components/common';

import { useMutateUpdateAssetAccess } from '@hooks/assets/assetAccess/useMutateUpdateAssetAccess';
import { useQueryAssetAccess } from '@hooks/assets/assetAccess/useQueryAssetAccess';

import { EDefaultRestrictedAccess, TDefaultRestrictedAccess } from '@enums/common';

import { MODAL_WIDTH_1000 } from '@constants/modals';

import AOAccessTable from './AOAccessTable';

import styles from './AOAccess.module.scss';

interface IAOAccessProps {
	organisationId: string;
}

const AOAccess = ({ organisationId }: IAOAccessProps) => {
	const [
		activeRadioButton,
		setActiveRadioButton,
	] = useState<TDefaultRestrictedAccess>(EDefaultRestrictedAccess.default);
	const [isTableVisible, setIsTableVisible] = useState(false);

	const isRestricted = activeRadioButton === EDefaultRestrictedAccess.restricted;

	const {
		assetAccess: { assetAccessCount = 0, assetAccess } = {},
		isLoadingAssetAccess,
	} = useQueryAssetAccess({ id: organisationId });

	const {
		updateAssetAccess,
		isLoadingUpdateAssetAccess,
	} = useMutateUpdateAssetAccess();

	const onChange = useCallback((name: TDefaultRestrictedAccess) => {
		updateAssetAccess({ assetAccess: name, id: organisationId });
		setActiveRadioButton(name);
	}, [organisationId, updateAssetAccess]);

	const openTable = useCallback(() => {
		if (!isRestricted) {
			return;
		}

		setIsTableVisible(true);
	}, [isRestricted]);

	const closeTable = useCallback(() => setIsTableVisible(false), []);

	const actionClasses = cn(styles.action, {
		[styles.actionActive]: isRestricted,
		[styles.actionDisabled]: !isRestricted,
	});

	useEffect(() => {
		setActiveRadioButton(assetAccess?.name || EDefaultRestrictedAccess.default);
	}, [assetAccess?.name]);

	const isLoading = isLoadingAssetAccess || isLoadingUpdateAssetAccess;

	return (
		<div className={styles.container}>
			{isLoading && <SpinnerOverlay />}
			<Modal
				isOpen={isTableVisible}
				onClose={closeTable}
				title='Select AOs'
				width={MODAL_WIDTH_1000}
			>
				<AOAccessTable id={organisationId}/>
			</Modal>
			<div className={styles.title}>AO Visibility for an MNO</div>
			<div className={styles.btnContainer}>
				<RadioButton
					checked={activeRadioButton === EDefaultRestrictedAccess.default}
					id={EDefaultRestrictedAccess.default}
					infoText='All AOs are visible (default)'
					label='All AOs are visible (default)'
					name={EDefaultRestrictedAccess.default}
					onChange={onChange}
					value={EDefaultRestrictedAccess.default}
				/>
			</div>
			<div className={styles.btnContainer}>
				<RadioButton
					checked={isRestricted}
					id={EDefaultRestrictedAccess.restricted}
					label='Restricted.'
					name={EDefaultRestrictedAccess.restricted}
					onChange={onChange}
					value={EDefaultRestrictedAccess.restricted}
				/>
				<div
					className={actionClasses}
					onClick={openTable}
				>
					Select AOs {isRestricted && `(${assetAccessCount})`}
				</div>
			</div>
		</div>
	);
};

export default memo(AOAccess);
